/**
 * The seams this package needs from its host app.
 *
 * Most of the residency model is shared, but two things are genuinely
 * app-owned:
 *
 *   - Which backend (dev vs prod) the app targets.
 *   - The Amplify singleton. Amplify keeps its configuration in module state,
 *     so `Amplify.configure` here would only be seen by `Auth` here. pnpm gives
 *     this package its own resolution of `@aws-amplify/*` (its peer set differs
 *     from either app's), so importing Amplify directly would configure a
 *     SECOND instance and leave the app's `Auth` unconfigured - the session
 *     would silently fail to materialise. The app passes its own instance in.
 *
 * Registration happens in each app's `BackendInterface` module body, alongside
 * the existing `configureCountryOriginEndpoint` call, so the seams are in place
 * before anything can trigger a residency change.
 */
export interface AmplifyBridge {
  /** The app's `Amplify.configure`. */
  configure: (config: Record<string, unknown>) => void;
  /** The app's `Auth.signIn`. */
  signIn: (username: string, password: string) => Promise<unknown>;
  /** The app's `Auth.signUp`. */
  signUp: (input: { username: string; password: string; attributes: Record<string, unknown> }) => Promise<unknown>;
}

export interface DataResidencyRuntime {
  /**
   * True when the app's resource environment is `dev`: it selects the dev
   * Cognito pools. This is the resource environment, not the deploy stage: a
   * prerelease build (release code on dev resources) sets it true while its
   * API roots use the `pre` base paths, which the apps derive
   * themselves. Each app registers it from its `deployStage` module (see
   * packages/common/lib/deployStageResolution.ts).
   */
  useDevelopmentBackend: boolean;

  /** The app's Amplify entry points - see AmplifyBridge. */
  amplify: AmplifyBridge;
}

function unregistered(name: string): never {
  throw new Error(
    `[data-residency] ${name} was called before the host app registered its ` +
      'Amplify bridge. Call configureDataResidencyRuntime() during app startup.',
  );
}

const runtime: DataResidencyRuntime = {
  useDevelopmentBackend: false,
  amplify: {
    configure: () => unregistered('Amplify.configure'),
    signIn: () => unregistered('Auth.signIn'),
    signUp: () => unregistered('Auth.signUp'),
  },
};

/**
 * Register some or all of the seams. Callable more than once so an app can
 * register each seam from the module that owns it.
 */
export function configureDataResidencyRuntime(overrides: Partial<DataResidencyRuntime>): void {
  Object.assign(runtime, overrides);
}

export function getDataResidencyRuntime(): DataResidencyRuntime {
  return runtime;
}

/** Test seam: restore the unregistered defaults. */
export function resetDataResidencyRuntimeForTests(): void {
  configureDataResidencyRuntime({
    useDevelopmentBackend: false,
    amplify: {
      configure: () => {},
      signIn: () => Promise.reject(new Error('signIn not stubbed')),
      signUp: () => Promise.reject(new Error('signUp not stubbed')),
    },
  });
}
